/**
 * The icon set, as inline SVG.
 *
 * ═══ Why not Unicode glyphs ══════════════════════════════════════════════════
 *
 * The first version of this console used characters — ▤ for Overview, ⊘ for Suppressions, ⌕ for
 * search, ⌘ for the shortcut hint. Every one of them rendered as a TOFU BOX (□) in the browser,
 * because Inter does not contain them and neither does the sans-serif fallback. Caught by taking an
 * actual screenshot of the running console; no assertion about text content can see it, which is
 * exactly why the screenshot pass exists.
 *
 * It is not a font-loading accident either: those code points are in the Geometric Shapes and
 * Miscellaneous Technical blocks, which UI sans fonts routinely omit. Relying on them means the icon
 * language depends on whichever font the OS substitutes — so it differs per platform, per browser,
 * and can vanish entirely. An inline SVG renders identically everywhere and costs nothing over the
 * wire because it is already in the bundle.
 *
 * ═══ House rules ═════════════════════════════════════════════════════════════
 *
 * · 16×16 viewBox, 1.5 stroke, `currentColor`. Colour comes from the surrounding text colour, so an
 *   icon is never a second place a status colour could disagree with the token.
 * · Stroked, not filled. Filled glyphs read as heavier than the 400/500 text beside them.
 * · `aria-hidden` by default. Every icon in this product sits next to a real text label — the rail,
 *   the buttons, the status chips. An icon that needed its own accessible name would mean the label
 *   was missing.
 */
import type { CSSProperties, ReactElement } from "react";

export type IconName =
  // Navigation
  | "overview"
  | "logs"
  | "events"
  | "suppressions"
  | "templates"
  | "campaigns"
  | "automations"
  | "contacts"
  | "lists"
  | "segments"
  | "domains"
  | "addresses"
  | "routing"
  | "team"
  | "keys"
  | "webhooks"
  | "docs"
  | "settings"
  | "help"
  // Controls and states
  | "chevron-down"
  | "chevron-right"
  | "search"
  | "menu"
  | "close"
  | "copy"
  | "check"
  | "arrow-right"
  | "arrow-up"
  | "arrow-down"
  | "warning"
  | "critical"
  | "info"
  | "sun"
  | "moon"
  | "external"
  | "refresh"
  | "lock"
  | "eye"
  | "eye-off";

/**
 * Path data per icon.
 *
 * Each entry is the inner markup of a 16×16 icon. Kept as data rather than as thirty components so
 * the set stays scannable and a caller cannot import half of it.
 */
const PATHS: Record<IconName, ReactElement> = {
  // Contacts: two people. Distinct from `team` (a workspace's staff) — these are the audience.
  contacts: (
    <>
      <circle cx="6" cy="6" r="2.5" />
      <path d="M1.5 13.5c0-2.4 2-4 4.5-4s4.5 1.6 4.5 4" />
      <path d="M11 4.2a2.3 2.3 0 0 1 0 4.4M12.5 13.5c0-1.9-.7-3.2-2-3.8" />
    </>
  ),
  // A list: rows each with a leading bullet. A named, ordered set someone curated.
  lists: (
    <>
      <circle cx="3" cy="4" r="1" />
      <circle cx="3" cy="8" r="1" />
      <circle cx="3" cy="12" r="1" />
      <path d="M6 4h8M6 8h8M6 12h8" />
    </>
  ),
  // A segment: a funnel. A larger set narrowed by rules to a smaller one.
  segments: (
    <>
      <path d="M2 3.5h12l-4.5 5v4l-3 1.5v-5.5z" />
    </>
  ),
  /*
   * A question in a circle. Deliberately not a lifebuoy or a speech bubble: a lifebuoy reads as
   * "emergency" and a bubble as "chat with an agent", and this screen is mostly things a customer can
   * answer for themselves without talking to anybody.
   */
  help: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M6.3 6.1a1.8 1.8 0 0 1 3.4.7c0 1.2-1.7 1.4-1.7 2.5" />
      <path d="M8 11.6v.01" />
    </>
  ),
  // A dashboard: one wide panel over two narrow ones. Reads as "summary of several things".
  overview: (
    <>
      <rect x="2" y="2.5" width="12" height="4.5" rx="1" />
      <rect x="2" y="9.5" width="5" height="4" rx="1" />
      <rect x="9" y="9.5" width="5" height="4" rx="1" />
    </>
  ),
  // A log: lines of unequal length, like a list of records rather than body text.
  logs: (
    <>
      <path d="M2.5 4h11M2.5 8h11M2.5 12h7" />
    </>
  ),
  // An event stream: a step change over time.
  events: <path d="M1.5 11h3l2-6 2.5 9L11.5 8h3" />,
  // Suppression: a blocked circle. The universal "not allowed", and unambiguous at 16px.
  suppressions: (
    <>
      <circle cx="8" cy="8" r="5.75" />
      <path d="M4 12 12 4" />
    </>
  ),
  // A template: a document with a repeated block, i.e. a reusable shape.
  templates: (
    <>
      <rect x="3" y="2" width="10" height="12" rx="1.5" />
      <path d="M5.5 5.5h5M5.5 8.5h5M5.5 11h3" />
    </>
  ),
  // A campaign: an outbound broadcast.
  campaigns: (
    <>
      <path d="M14 2.5 7 8.5 2 6.5z" />
      <path d="M7 8.5 14 2.5l-2.5 11z" />
    </>
  ),
  // Automation: a branch. One trigger, two outcomes.
  automations: (
    <>
      <path d="M2.5 4.5h4l3 3h4M2.5 11.5h4l3-3" />
      <path d="M11.5 5 13.5 7.5 11.5 10" />
    </>
  ),
  // A domain: a globe, because this is DNS and not a website.
  domains: (
    <>
      <circle cx="8" cy="8" r="5.75" />
      <path d="M2.3 8h11.4M8 2.25c1.6 1.6 2.4 3.6 2.4 5.75S9.6 12.15 8 13.75C6.4 12.15 5.6 10.15 5.6 8S6.4 3.85 8 2.25Z" />
    </>
  ),
  // An address: the at-sign, drawn rather than typed so its weight matches the other icons.
  addresses: (
    <>
      <circle cx="8" cy="8" r="2.4" />
      <path d="M10.4 5.6v3.1c0 1 .6 1.6 1.5 1.6 1.2 0 1.9-1.1 1.9-2.7A5.8 5.8 0 1 0 10.6 13.4" />
    </>
  ),
  // Inbound routing: mail arriving into a tray.
  routing: (
    <>
      <path d="M2.5 9.5V13a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1V9.5h-3l-1 1.5h-3l-1-1.5z" />
      <path d="M8 2v5.5M5.75 5.5 8 7.75 10.25 5.5" />
    </>
  ),
  // A key.
  // Two people: the workspace team. One head in front, one behind, so it reads as "more than me"
  // at 14px rather than as a single silhouette.
  team: (
    <>
      <circle cx="6" cy="5.5" r="2.5" />
      <path d="M1.5 14c0-2.5 2-4.25 4.5-4.25S10.5 11.5 10.5 14" />
      <path d="M10.75 3.5a2.5 2.5 0 0 1 0 4.75M12 9.9c1.6.5 2.5 2 2.5 4.1" />
    </>
  ),
  keys: (
    <>
      <circle cx="5" cy="11" r="2.75" />
      <path d="M7 9 13.5 2.5M11 5l2 2M12.5 3.5l2 2" />
    </>
  ),
  // A webhook: a callback leaving our system for theirs.
  webhooks: (
    <>
      <path d="M9.5 2.5h4v4" />
      <path d="M13.5 2.5 8 8" />
      <path d="M12 9.5v3a1 1 0 0 1-1 1H3.5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3" />
    </>
  ),
  // Reference docs.
  docs: (
    <>
      <path d="M2.5 3.5A1 1 0 0 1 3.5 2.5H7A1.5 1.5 0 0 1 8 4v9.5a1.2 1.2 0 0 0-1-.9H3.5a1 1 0 0 1-1-1z" />
      <path d="M13.5 3.5a1 1 0 0 0-1-1H9A1.5 1.5 0 0 0 8 4v9.5a1.2 1.2 0 0 1 1-.9h3.5a1 1 0 0 0 1-1z" />
    </>
  ),
  settings: (
    <>
      <circle cx="8" cy="8" r="2.25" />
      <path d="M8 1.5v1.6M8 12.9v1.6M2.9 2.9l1.2 1.2M11.9 11.9l1.2 1.2M1.5 8h1.6M12.9 8h1.6M2.9 13.1l1.2-1.2M11.9 4.1l1.2-1.2" />
    </>
  ),

  "chevron-down": <path d="M4 6.25 8 10.25l4-4" />,
  "chevron-right": <path d="M6.25 4l4 4-4 4" />,
  search: (
    <>
      <circle cx="7.25" cy="7.25" r="4.5" />
      <path d="M10.6 10.6 14 14" />
    </>
  ),
  /* Opens the navigation drawer below 900px, where the rail is off-canvas. */
  menu: <path d="M2.5 4h11M2.5 8h11M2.5 12h11" />,
  close: <path d="M4 4l8 8M12 4l-8 8" />,
  copy: (
    <>
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.25" />
      <path d="M10.5 5.5v-2a1 1 0 0 0-1-1h-6a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2" />
    </>
  ),
  check: <path d="M3 8.5 6.25 11.75 13 5" />,
  "arrow-right": <path d="M3 8h10M9.25 4.25 13 8l-3.75 3.75" />,
  "arrow-up": <path d="M8 13V3M4.25 6.75 8 3l3.75 3.75" />,
  "arrow-down": <path d="M8 3v10M4.25 9.25 8 13l3.75-3.75" />,
  // A triangle for "attention", visually distinct in silhouette from the octagon below, so the two
  // severities differ by SHAPE and not only by colour.
  warning: (
    <>
      <path d="M8 2.25 14.5 13.5h-13z" />
      <path d="M8 6.25v3.4" />
      <circle cx="8" cy="11.6" r=".55" fill="currentColor" stroke="none" />
    </>
  ),
  // An octagon for "critical" — the stop-sign silhouette.
  critical: (
    <>
      <path d="M5.6 1.75h4.8l3.85 3.85v4.8l-3.85 3.85H5.6L1.75 10.4V5.6z" />
      <path d="M8 5.25v3.4" />
      <circle cx="8" cy="10.75" r=".55" fill="currentColor" stroke="none" />
    </>
  ),
  /*
   * A circle for "info", and the mark is an `i` rather than a `!`: dot ABOVE, stem below.
   *
   * That inversion is the point. Triangle, octagon and circle already separate the three severities by
   * shape, per the note above, but a circle carrying an exclamation mark still reads as a warning at
   * 16px — so the glyph inside has to disagree too.
   */
  info: (
    <>
      <circle cx="8" cy="8" r="6.25" />
      <circle cx="8" cy="5.25" r=".55" fill="currentColor" stroke="none" />
      <path d="M8 7.4v3.35" />
    </>
  ),
  sun: (
    <>
      <circle cx="8" cy="8" r="3" />
      <path d="M8 1v1.5M8 13.5V15M2 8H.5M15.5 8H14M3.75 3.75 2.7 2.7M13.3 13.3l-1.05-1.05M3.75 12.25 2.7 13.3M13.3 2.7l-1.05 1.05" />
    </>
  ),
  moon: <path d="M13.5 9.7A5.8 5.8 0 0 1 6.3 2.5a5.8 5.8 0 1 0 7.2 7.2Z" />,
  external: (
    <>
      <path d="M9.5 2.5h4v4M13.5 2.5 8 8" />
      <path d="M12 9.5v3a1 1 0 0 1-1 1H3.5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3" />
    </>
  ),
  refresh: (
    <>
      <path d="M13.5 8a5.5 5.5 0 1 1-1.9-4.16" />
      <path d="M13.6 1.9v3.2h-3.2" />
    </>
  ),
  lock: (
    <>
      <rect x="3.25" y="7" width="9.5" height="6.75" rx="1.25" />
      <path d="M5.75 7V5.25a2.25 2.25 0 0 1 4.5 0V7" />
    </>
  ),
  /*
   * Show / hide a password.
   *
   * These exist because the sign-in form was reusing `sun` and `moon` for the reveal toggle — which put
   * a sun icon inside the password field, where it reads as a theme switch. Caught by looking at the
   * screenshot; no assertion about behaviour would have noticed, because the button worked.
   */
  eye: (
    <>
      <path d="M1.4 8s2.4-4.25 6.6-4.25S14.6 8 14.6 8s-2.4 4.25-6.6 4.25S1.4 8 1.4 8Z" />
      <circle cx="8" cy="8" r="2" />
    </>
  ),
  "eye-off": (
    <>
      <path d="M6.3 3.95A6.9 6.9 0 0 1 8 3.75c4.2 0 6.6 4.25 6.6 4.25a12 12 0 0 1-1.85 2.4" />
      <path d="M11.3 11.55A6.6 6.6 0 0 1 8 12.25C3.8 12.25 1.4 8 1.4 8a12 12 0 0 1 2.9-3.25" />
      <path d="M6.6 6.6a2 2 0 0 0 2.8 2.8" />
      <path d="M2 2l12 12" />
    </>
  ),
};

export function Icon({
  name,
  size = 16,
  style,
  className = "",
  strokeWidth = 1.5,
}: {
  name: IconName;
  size?: number;
  style?: CSSProperties;
  className?: string;
  strokeWidth?: number;
}) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      className={className}
      style={{ flex: "0 0 auto", display: "block", ...style }}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
